/** Commande segmentée (design system, CommandeSegmentee) : un choix parmi deux à quatre. */
import { useRef, type KeyboardEvent } from 'react';
import { cx } from '../outils';
import { Icone, type NomIcone } from './Icone';

export interface OptionSegment<V extends string = string> {
  valeur: V;
  libelle: string;
  icone?: NomIcone;
}

export interface CommandeSegmenteeProps<V extends string = string> {
  options: OptionSegment<V>[];
  valeur: V;
  onChoisir: (valeur: V) => void;
  etiquette?: string;
  etiquettePar?: string;
  taille?: 'normale' | 'petite';
  iconesSeules?: boolean;
  large?: boolean;
  desactivee?: boolean;
}

export function CommandeSegmentee<V extends string>({
  options,
  valeur,
  onChoisir,
  etiquette,
  etiquettePar,
  taille = 'normale',
  iconesSeules = false,
  large = false,
  desactivee = false,
}: CommandeSegmenteeProps<V>) {
  const groupe = useRef<HTMLDivElement>(null);

  function clavier(e: KeyboardEvent<HTMLDivElement>) {
    const i = options.findIndex((o) => o.valeur === valeur);
    let j = i;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
      j = (i - 1 + options.length) % options.length;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = options.length - 1;
    else return;
    e.preventDefault();
    onChoisir(options[j]!.valeur);
    groupe.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[j]?.focus();
  }

  return (
    <div
      ref={groupe}
      className={cx(
        'fc-commande-segmentee',
        taille === 'petite' && 'fc-commande-segmentee--petite',
        iconesSeules && 'fc-commande-segmentee--icones',
        large && 'fc-commande-segmentee--large',
      )}
      role="radiogroup"
      aria-label={etiquette}
      aria-labelledby={etiquettePar}
      aria-disabled={desactivee || undefined}
      onKeyDown={clavier}
    >
      {options.map((option) => (
        <button
          key={option.valeur}
          type="button"
          className="fc-commande-segmentee__segment"
          role="radio"
          aria-checked={option.valeur === valeur}
          aria-label={iconesSeules ? option.libelle : undefined}
          title={iconesSeules ? option.libelle : undefined}
          tabIndex={option.valeur === valeur ? 0 : -1}
          disabled={desactivee}
          onClick={() => onChoisir(option.valeur)}
        >
          {option.icone && <Icone nom={option.icone} />}
          {!iconesSeules && option.libelle}
        </button>
      ))}
    </div>
  );
}
